import React from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { P } from '@components/shared/Typography';
import { HEMP_SVG, TAMPON_BLOOD_DROPLET_COUNTS } from '@constants';
import BoxCounter from './BoxContentCounter';
import { StyledSimpleBoxWrapper } from '@components/shared/SharedStyles';
import { TamponType } from '@contracts';
import DropletIcons from '@components/products/DropletIcons';

interface Props {
  variant: TamponType;
  count: number;
  updateVariants: (key: string, count: number) => void;
  showTamponIcon?: boolean;
  tamponsFull: boolean;
}

export default function BoxContentSectionTampon({ variant, count, updateVariants, showTamponIcon = true, tamponsFull }: Props) {
  const { t } = useTranslation();
  const isSuper = variant.includes('super');
  const isCBD = variant.includes('cbd');

  return (
    <Container className="box-content-section">
      <StyledSimpleBoxWrapper>
        <CounterRow $showTamponIcon={showTamponIcon} $hempIcon={isCBD}>
          <Flex>
            <IconWrapper>
              <DropletIcons dropsCount={TAMPON_BLOOD_DROPLET_COUNTS[variant]} />
              {isCBD && <img style={{ marginLeft: '6px' }} src={HEMP_SVG} />}
            </IconWrapper>
            <PStyled>
              <span>{isCBD ? t('components.comfort-cbd') : t('components.organic')}</span>
              <span className="bold-line">{isSuper ? t('components.super') : t('components.regular')}</span>
            </PStyled>
          </Flex>
          <BoxCounter
            count={count}
            add={() => {
              return updateVariants(variant, count + 1);
            }}
            remove={() => {
              return updateVariants(variant, count - 1);
            }}
            disableAdd={tamponsFull}
            disableRemove={count === 0}
            type={`${variant}`}
          />
        </CounterRow>
      </StyledSimpleBoxWrapper>
    </Container>
  );
}

const IconWrapper = styled.div`
  min-width: 55px;
  display: flex;
  justify-content: center;

  img {
    height: 15px;
  }

  @media (max-width: 768px) {
    min-width: 28px;
  }
`;

const PStyled = styled(P)`
  font-family: BrownStd;
  font-style: normal;
  line-height: 19px;
  letter-spacing: 0;
  text-align: left;
  flex-grow: 1;
  margin-left: 8px;
  color: ${Colors.navyBlue};

  & span {
    font-size: 14px;
    font-weight: 400;
  }

  & .bold-line {
    font-weight: 700;
    display: block;
    font-size: 14px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

interface IFlex {
  $showTamponIcon?: boolean;
  $hempIcon?: boolean;
}

const Flex = styled.div<IFlex>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 100px;
  width: ${({ $hempIcon }) => ($hempIcon ? 'auto' : '50%')};
`;

const CounterRow = styled.div<IFlex>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;

  @media only screen and (max-width: 768px) {
    padding: ${({ $showTamponIcon }) => ($showTamponIcon ? '15px' : '22px 28px')};
  }
`;
